// src/components/LoginPage.jsx — Merged Login and Register component
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { departmentAPI, otpAPI } from '../services/api';
import api from '../services/api';
import medicalImage from "../Images/Medical.png";
import '../styles/LoginPage.css';
import '../styles/RegisterPage.css';
import { getDashboardPathForUser } from '../constants/roleRoutes';

// ─── Student search helper ────────────────────────────────────────────────────
const searchStudents = async (q) => {
  if (!q || q.trim().length < 2) return [];
  const res = await api.get('/auth/students/search/', { params: { q } });
  return Array.isArray(res.data) ? res.data : [];
};

// ─── StudentSearchDropdown ────────────────────────────────────────────────────
function StudentSearchDropdown({ selectedStudents, onAdd, onRemove }) {
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(false);
  const timerRef = useRef(null);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(timerRef.current);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const data = await searchStudents(q);
        setResults(data);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const add = (student) => {
    onAdd(student);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInput}
          placeholder="Search by student name or enrollment number…"
          className="w-full h-[58px] pl-12 pr-12 rounded-[14px] border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 text-[14px] font-semibold text-slate-800 outline-none placeholder-slate-400"
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">🔍</span>
        {loading && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 font-medium">
            Searching…
          </span>
        )}
      </div>

      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div className="absolute z-[1000] top-full mt-2 left-0 right-0 bg-white border border-slate-100 rounded-[14px] shadow-xl max-h-[260px] overflow-y-auto">
          {results.map(s => {
            const already = selectedStudents.some(x => x.id === s.id);
            return (
              <div key={s.id}
                onMouseDown={(e) => { e.preventDefault(); if (!already) add(s); }}
                className={`p-3.5 cursor-pointer border-b border-slate-50 last:border-b-0 flex items-center gap-3 transition-colors ${already ? 'bg-slate-50 cursor-default' : 'hover:bg-teal-50/50'}`}
              >
                <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center text-lg shrink-0">🎓</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[13px] text-slate-800">{s.full_name}</div>
                  <div className="text-[11px] text-slate-400 font-medium">
                    {s.student_id && <span>ID: {s.student_id} · </span>}
                    {s.course_display} {s.department && `· ${s.department}`}
                  </div>
                </div>
                {already
                  ? <span className="text-[11px] text-emerald-600 font-bold">✓ Added</span>
                  : <span className="text-[11px] text-teal-600 font-bold">+ Add</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Selected students chips */}
      {selectedStudents.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedStudents.map(s => (
            <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-100 rounded-full text-[12px] font-bold text-teal-800">
              🎓 {s.full_name}
              {s.student_id && <span className="opacity-70 font-normal">({s.student_id})</span>}
              <button type="button" onClick={() => onRemove(s.id)}
                className="hover:text-teal-950 font-extrabold text-sm leading-none ml-1 transition-colors"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Custom Inline SVG Logo ───────────────────────────────────────────────────
const Logo = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Shield */}
    <path d="M60 18C78 18 94 28 94 48C94 72 74 96 60 106C46 96 26 72 26 48C26 28 42 18 60 18Z" fill="#008395" />
    {/* White Cross inside Shield */}
    <path d="M60 38V74M42 56H78" stroke="white" strokeWidth="8" strokeLinecap="round" />
    {/* Graduation Cap on top of Shield */}
    <path d="M60 8L92 22L60 36L28 22L60 8Z" fill="#00363a" />
    <path d="M40 28V46C40 54 50 60 60 60C70 60 80 54 80 46V28" stroke="#00363a" strokeWidth="3" fill="none" />
    {/* Tassel */}
    <path d="M92 22V40M92 40L88 44" stroke="#008395" strokeWidth="2.5" />
  </svg>
);

// ─── Features Configuration ───────────────────────────────────────────────────
const modulesList = [
  {
    title: "Student Information System",
    desc: "Manage student data and records",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    )
  },
  {
    title: "Admission Management",
    desc: "Streamline admission process",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    )
  },
  {
    title: "Academic Management",
    desc: "Curriculum, courses & evaluations",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
      </svg>
    )
  },
  {
    title: "Faculty Management",
    desc: "Manage faculty & departments",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M9 21v-2a4 4 0 0 0-3-3.87" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    )
  },
  {
    title: "Timetable & Scheduling",
    desc: "Smart scheduling made simple",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    )
  },
  {
    title: "Attendance Management",
    desc: "Track attendance seamlessly",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    )
  },
  {
    title: "Examination Management",
    desc: "Conduct & manage exams easily",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  },
  {
    title: "Clinical & Hospital Management",
    desc: "Conduct, OPD, IPD & EMR",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    )
  },
  {
    title: "Finance & Fee Management",
    desc: "Fees, payments & financial control",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    )
  },
  {
    title: "Library Management",
    desc: "Books, resources & circulation",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    )
  },
  {
    title: "Hostel & Inventory",
    desc: "Hostel allocation & inventory tracking",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="9" y1="3" x2="9" y2="21" />
        <line x1="15" y1="3" x2="15" y2="21" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="3" y1="15" x2="21" y2="15" />
      </svg>
    )
  },
  {
    title: "Communication System",
    desc: "Notices, alerts & messaging",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    )
  },
  {
    title: "Reports & Analytics",
    desc: "Real-time insights & data analytics",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    )
  }
];



// ─── Main LoginPage (Handles both Login and Registration) ──────────────────────
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, error: authError, user } = useAuth();

  const isRegister = location.pathname === '/register';

  // ─── LOGIN STATES ───────────────────────────────────────────────────────────
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
  });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ─── REGISTER STATES ────────────────────────────────────────────────────────
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    role: 'STUDENT',
    phone_number: '',
    course: '',
    entrance_score: '',
    student_department: '',
    assigned_professor: '',
    faculty_department: '',
    faculty_subrole: '',
    relationship: 'GUARDIAN',
    evaluator_university: '',
    evaluator_state: '',
  });

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [departments,      setDepartments]      = useState([]);
  const [professors,       setProfessors]       = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingProfessors,  setLoadingProfessors]  = useState(false);
  const [registerErrors,   setRegisterErrors]   = useState({});
  const [registerLoading,  setRegisterLoading]  = useState(false);
  const [showConfirmPw,    setShowConfirmPw]    = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [requiresApproval,    setRequiresApproval]    = useState(false);

  // ─── OTP STATES ────────────────────────────────────────────────────────────
  const [otpStep,    setOtpStep]    = useState('idle'); // 'idle' | 'sending' | 'sent' | 'verifying' | 'verified'
  const [otp,        setOtp]        = useState('');
  const [otpError,   setOtpError]   = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [countdown,  setCountdown]  = useState(0);
  const countdownRef = useRef(null);

  const emailVerified = otpStep === 'verified';

  useEffect(() => {
    return () => clearInterval(countdownRef.current);
  }, []);

  const startCountdown = (seconds = 30) => {
    setCountdown(seconds);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async () => {
    setOtpError('');
    setOtpSuccess('');
    const emailVal = registerData.email.trim();
    if (!emailVal || !/\S+@\S+\.\S+/.test(emailVal)) {
      setOtpError('Please enter a valid email address first.');
      return;
    }
    setOtpStep('sending');
    try {
      const res = await otpAPI.sendOTP(emailVal);
      if (res && res.otp) {
        console.log('--- REGISTER OTP ---', res.otp);
      }
      setOtpStep('sent');
      setOtpSuccess('OTP sent! Check your email inbox (and terminal log for dev).');
      startCountdown(30);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.email?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        'Failed to send OTP.';
      setOtpError(msg);
      setOtpStep('idle');
    }
  };

  const handleVerifyOTP = async (codeParam) => {
    const otpCode = (typeof codeParam === 'string' ? codeParam : otp).trim();
    setOtpError('');
    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      setOtpError('Please enter the 6-digit numeric OTP.');
      return;
    }
    setOtpStep('verifying');
    try {
      await otpAPI.verifyOTP(registerData.email.trim(), otpCode);
      setOtpStep('verified');
      setOtpSuccess('Email verified successfully!');
      setOtpError('');
    } catch (err) {
      const msg =
        err?.response?.data?.error || 'Incorrect OTP. Please try again.';
      setOtpError(msg);
      setOtpStep('sent');
    }
  };

  const PG_DEPT_CODES = ['ANAT','ANES','BIOC','COMM','DERM','ENT','MED','SURG','MICR','OBGYN','OPHT','ORTHO','PATH','PED','PHAR','PHYS','PSY','RAD','PULM'];
  const SS_DEPT_CODES = ['CARD','GAST','NEUR','URO'];

  const isDeptDisabled = registerData.role === 'STUDENT' && registerData.course === 'MBBS';

  const getFilteredDepts = () => {
    if (registerData.role !== 'STUDENT') return departments;
    if (registerData.course === 'PG')               return departments.filter(d => PG_DEPT_CODES.includes(d.code));
    if (registerData.course === 'SUPER_SPECIALITY') return departments.filter(d => SS_DEPT_CODES.includes(d.code));
    return departments;
  };

  // ─── AUTH REDIRECT ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(getDashboardPathForUser(user), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Load departments if we go to Register page
  useEffect(() => {
    if (isRegister) {
      fetchDepartments();
    }
  }, [isRegister]);

  // Load professors dynamically based on department selection
  useEffect(() => {
    if (registerData.student_department && registerData.role === 'STUDENT' && registerData.course !== 'MBBS') {
      fetchProfessors(registerData.student_department);
    }
  }, [registerData.student_department, registerData.role, registerData.course]);

  // Clean department professor if course is MBBS
  useEffect(() => {
    if (registerData.role === 'STUDENT' && registerData.course === 'MBBS') {
      setRegisterData(p => ({ ...p, student_department: '', assigned_professor: '' }));
      setProfessors([]);
    }
  }, [registerData.course]);

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const data = await departmentAPI.getDepartments();
      setDepartments(Array.isArray(data) ? data : (data.results || []));
    } catch { setRegisterErrors(p => ({ ...p, departments: 'Failed to load departments' })); }
    finally { setLoadingDepartments(false); }
  };

  const fetchProfessors = async (deptId) => {
    try {
      setLoadingProfessors(true);
      setProfessors([]);
      setRegisterData(p => ({ ...p, assigned_professor: '' }));
      const data = await departmentAPI.getFacultyByDepartment(deptId);
      setProfessors(data);
    } catch { setRegisterErrors(p => ({ ...p, professors: 'Failed to load professors' })); }
    finally { setLoadingProfessors(false); }
  };

  // ─── HANDLERS ───────────────────────────────────────────────────────────────
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (loginErrors[name]) {
      setLoginErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(p => ({ ...p, [name]: value }));
    if (registerErrors[name]) {
      setRegisterErrors(p => ({ ...p, [name]: '' }));
    }
    if (name === 'email') {
      setOtpStep('idle');
      setOtp('');
      setOtpError('');
      setOtpSuccess('');
      clearInterval(countdownRef.current);
      setCountdown(0);
    }
  };

  // ─── VALIDATIONS ────────────────────────────────────────────────────────────
  const validateLoginForm = () => {
    const newErrors = {};
    if (!loginData.username.trim()) {
      newErrors.username = 'Username / Email is required';
    }
    if (!loginData.password) {
      newErrors.password = 'Password is required';
    } else if (loginData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegisterForm = () => {
    const e = {};
    if (!registerData.username.trim())               e.username = 'Username is required';
    else if (registerData.username.length < 3)       e.username = 'Min 3 characters';
    if (!registerData.email.trim())                  e.email    = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(registerData.email)) e.email = 'Invalid email';
    else if (!emailVerified)                         e.email    = 'Please verify your email via OTP before registering.';
    if (!registerData.password)                      e.password = 'Password is required';
    else if (registerData.password.length < 8)       e.password = 'Min 8 characters';
    if (!registerData.password_confirm)              e.password_confirm = 'Please confirm password';
    else if (registerData.password !== registerData.password_confirm) e.password_confirm = 'Passwords do not match';
    if (!registerData.first_name.trim())             e.first_name = 'First name required';
    if (!registerData.last_name.trim())              e.last_name  = 'Last name required';

    if (registerData.role === 'STUDENT') {
      if (!registerData.course.trim()) e.course = 'Course is required';
      if (['PG','SUPER_SPECIALITY'].includes(registerData.course)) {
        if (!registerData.student_department) e.student_department = 'Department is required';
        if (!registerData.assigned_professor) e.assigned_professor = 'Professor is required';
      }
    }

    if (registerData.role === 'FACULTY') {
      if (!registerData.faculty_department) e.faculty_department = 'Department is required';
      if (!registerData.faculty_subrole)    e.faculty_subrole    = 'Sub-role is required';
    }

    if (registerData.role === 'PARENT') {
      if (selectedStudents.length === 0) e.linked_students = 'Please link at least one student';
    }

    if (registerData.role === 'PAPER_EVALUATOR') {
      if (!registerData.evaluator_university.trim()) e.evaluator_university = 'University is required';
      if (!registerData.evaluator_state.trim())      e.evaluator_state = 'State is required';
    }

    setRegisterErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── SUBMISSIONS ────────────────────────────────────────────────────────────
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLoginForm()) return;
    setLoginLoading(true);
    const result = await login(loginData.username, loginData.password);
    if (result.success) {
      const redirectPath = getDashboardPathForUser(result.user);
      if (redirectPath !== '/login') {
        navigate(redirectPath, { replace: true });
      } else {
        setLoginErrors({ submit: 'Invalid user role. Please contact administrator.' });
      }
    } else {
      setLoginErrors({ submit: result.error });
    }
    setLoginLoading(false);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validateRegisterForm()) return;
    setRegisterLoading(true);

    const payload = { ...registerData };
    if (registerData.role === 'PARENT') {
      payload.linked_student_ids = selectedStudents.map(s => s.id);
    }

    const result = await register(payload);
    if (result.success) {
      setRegistrationSuccess(true);
      if (result.requiresApproval) {
        setRequiresApproval(true);
      } else {
        navigate(getDashboardPathForUser(result.user), { replace: true });
      }
    } else {
      setRegisterErrors({ submit: result.error });
    }
    setRegisterLoading(false);
  };

  // Render Success screen if register succeeded and COE/Admin approval is required
  if (isRegister && registrationSuccess && requiresApproval) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center bg-gradient-to-tr from-slate-900/40 via-slate-900/10 to-teal-900/30 backdrop-blur-[4px] relative p-6 font-['Plus_Jakarta_Sans']">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
          style={{ backgroundImage: `url(${medicalImage})`, filter: 'blur(8px) brightness(0.95)' }}
        />
        <div className="bg-white/95 backdrop-blur-xl rounded-[32px] p-12 max-w-[550px] w-full shadow-[0_24px_64px_-12px_rgba(15,23,42,0.18)] border border-white/80 text-center animate-fade-in flex flex-col gap-8">
          <div className="flex justify-center">
            <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/25">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="m9 12 2 2 4-4" />
              </svg>
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Registration Successful!</h1>
            <p className="text-slate-600 font-bold mt-3 text-[17px]">Your account has been created successfully.</p>
            {registerData.role === 'PAPER_EVALUATOR' ? (
              <p className="text-slate-400 font-medium mt-1 text-[14px]">It requires approval from the Controller of Examinations (COE) before you can log in.</p>
            ) : (
              <p className="text-slate-400 font-medium mt-1 text-[14px]">It requires approval from the administrator before you can log in.</p>
            )}
          </div>
          <div className="bg-teal-50/50 border border-teal-100/50 rounded-2xl p-5 flex items-start gap-4 text-left">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            {registerData.role === 'PAPER_EVALUATOR' ? (
              <p className="text-[13.5px] font-bold text-teal-800 leading-relaxed">You will be notified once the COE approves your request.</p>
            ) : (
              <p className="text-[13.5px] font-bold text-teal-800 leading-relaxed">You will be notified once your account is approved.</p>
            )}
          </div>
          <div>
            <Link to="/login" className="w-full h-[60px] rounded-[14px] bg-gradient-to-r from-[#1b6b7a] to-[#0c4e5a] text-white font-bold text-[16px] shadow-lg shadow-teal-900/10 hover:shadow-xl hover:shadow-teal-900/20 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const filteredDepts = getFilteredDepts();

  const leftColumnModules = modulesList.slice(0, 7);
  const rightColumnModules = modulesList.slice(7);

  const handleRoleSelect = (roleName) => {
    const credentials = {
      Student: { username: 'srikar', password: 'password123' },
      Faculty: { username: 'professor', password: 'password123' },
      Doctor: { username: 'commed', password: 'password123' },
      Admin: { username: 'admin', password: 'password123' },
    };
    if (credentials[roleName]) {
      setLoginData(credentials[roleName]);
      setLoginErrors({});
    }
  };

  return (
    <div className="min-h-screen lg:h-screen w-full flex flex-col lg:flex-row select-none font-sans overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* Left 60% Side: Fixed Background Campus Image & Branding overlay */}
      <div className="hidden lg:flex lg:w-[60vw] lg:max-w-[60vw] shrink-0 h-full relative overflow-hidden flex-col justify-center text-slate-800">
        {/* Campus background image (fixed inside the left 60% container only) */}
        <div 
          className="absolute inset-0 z-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${medicalImage})` }}
        />

        
        {/* Left Content Wrapper */}
        <div className="relative z-10 w-full max-w-[620px] ml-0 pl-16 md:pl-20 pr-6 py-8 flex flex-col justify-center">
          
          {/* Logo & Sub-Branding */}
          <div className="flex items-center gap-3.5 mb-8">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg border border-slate-100/50">
              <Logo size={36} />
            </div>
            <div className="flex flex-col text-left">
              <span className="font-extrabold text-[22px] text-slate-900 leading-tight tracking-wide">MEDICAL ERP</span>
              <span className="font-bold text-[12px] text-slate-600 leading-tight mt-0.5">Medical College & Hospital Management System</span>
            </div>
          </div>

          {/* Hero text section */}
          <div className="flex flex-col text-left">
            <h1 className="text-[40px] lg:text-[46px] font-black text-slate-900 leading-[1.1] tracking-tight">
              One Platform.
              <br />
              <span className="text-[#0D8AA8]">Complete Excellence.</span>
            </h1>
            <div className="w-16 h-1.5 bg-[#0D8AA8] rounded-full my-6" />
            <p className="text-[17px] text-slate-800 font-semibold leading-relaxed max-w-[480px]">
              Unified ERP for Academics, Administration, Hospital & Beyond.
            </p>
          </div>

          {/* Glassmorphism Feature List Card (white background for readability) */}
          <div className="mt-8 bg-white/75 backdrop-blur-[8px] border border-white/50 shadow-[0_16px_40px_rgba(15,23,42,0.08)] rounded-[24px] p-6 lg:p-7 max-w-[560px]">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-left">
              {/* Left Column Features */}
              <div className="flex flex-col gap-4">
                {leftColumnModules.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-teal-50/50 flex items-center justify-center text-[#0D8AA8] shrink-0 border border-teal-100/50">
                      {item.icon}
                    </div>
                    <span className="text-[13px] font-bold text-slate-800 leading-tight">
                      {item.title}
                    </span>
                  </div>
                ))}
              </div>

              {/* Right Column Features */}
              <div className="flex flex-col gap-4">
                {rightColumnModules.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-teal-50/50 flex items-center justify-center text-[#0D8AA8] shrink-0 border border-teal-100/50">
                      {item.icon}
                    </div>
                    <span className="text-[13px] font-bold text-slate-800 leading-tight">
                      {item.title}
                    </span>
                  </div>
                ))}
              </div>
          </div>
        </div>
      </div>
    </div>

      {/* Right 40% Side: Solid Scrollable Panel for Login & Registration */}
      <div className="w-full lg:w-[40vw] lg:max-w-[40vw] shrink-0 min-h-screen lg:min-h-0 lg:h-full relative z-10 bg-slate-50 lg:bg-white flex flex-col justify-start overflow-y-auto lg:border-l lg:border-slate-100 custom-scrollbar py-6 lg:py-8">
        <div className="w-full max-w-[480px] mx-auto px-6 sm:px-8 flex flex-col gap-5 my-auto">

            {/* Logo Badge */}
            <div className="flex justify-center lg:flex-shrink-0">
              <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shadow-sm">
                <Logo size={42} />
              </div>
            </div>

            {/* Header Titles */}
            <div className="flex flex-col text-center lg:flex-shrink-0">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                {isRegister ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-[13px] font-bold text-slate-400 mt-1">
                {isRegister ? 'Join the Medical ERP System' : 'Sign in to access your ERP dashboard'}
              </p>
            </div>

            {/* Error banners */}
            {!isRegister && (loginErrors.submit || authError) && (
              <div className="auth-error-banner lg:flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{loginErrors.submit || authError}</span>
              </div>
            )}

            {isRegister && registerErrors.submit && (
              <div className="auth-error-banner lg:flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{registerErrors.submit}</span>
              </div>
            )}

            {/* Form Fields */}
            {!isRegister ? (
              <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4 lg:flex-shrink-0">

                {/* Username Input */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[12px] font-extrabold text-slate-500 uppercase tracking-wider">Username / Email</label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-slate-400">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      name="username"
                      value={loginData.username}
                      onChange={handleLoginChange}
                      className={`w-full pl-11 pr-4 py-3 border ${loginErrors.username ? 'border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-[#0D8AA8] focus:ring-[#0D8AA8]/10'} focus:ring-4 outline-none rounded-xl text-slate-800 text-[14px] font-semibold transition-all bg-slate-50/50 hover:bg-white focus:bg-white`}
                      placeholder="Enter your username or email"
                      autoComplete="username"
                    />
                  </div>
                  {loginErrors.username && <span className="text-red-500 text-[11.5px] font-bold mt-1">{loginErrors.username}</span>}
                </div>

                {/* Password Input */}
                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[12px] font-extrabold text-slate-500 uppercase tracking-wider">Password</label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-slate-400">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      className={`w-full pl-11 pr-10 py-3 border ${loginErrors.password ? 'border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-[#0D8AA8] focus:ring-[#0D8AA8]/10'} focus:ring-4 outline-none rounded-xl text-slate-800 text-[14px] font-semibold transition-all bg-slate-50/50 hover:bg-white focus:bg-white`}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {loginErrors.password && <span className="text-red-500 text-[11.5px] font-bold mt-1">{loginErrors.password}</span>}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between mt-1.5">
                  <label className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-[13px] font-bold cursor-pointer select-none">
                    <input type="checkbox" className="rounded border-slate-300 text-[#0D8AA8] focus:ring-[#0D8AA8]" />
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="text-[#0D8AA8] hover:text-[#0A6D8C] text-[13px] font-bold transition-colors">
                    Forgot Password?
                  </Link>
                </div>

                {/* Sign In Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-[#0D8AA8] hover:bg-[#0A6D8C] active:scale-[0.99] text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg shadow-teal-500/10 flex items-center justify-center relative text-[15px] cursor-pointer disabled:bg-slate-400 disabled:shadow-none disabled:cursor-not-allowed mt-2"
                  disabled={loginLoading}
                >
                  {loginLoading ? 'Signing In...' : 'Sign In'}
                  <span className="absolute right-5 flex items-center text-white/95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegisterSubmit} className="flex flex-col gap-4.5 w-full">

                  {/* First Name & Last Name */}
                  <div className="form-row">
                    <div className="custom-input-group">
                      <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>First Name *</label>
                      <div className="custom-input-wrapper">
                        <span className="custom-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          name="first_name"
                          value={registerData.first_name}
                          onChange={handleRegisterChange}
                          className={`custom-form-input ${registerErrors.first_name ? 'input-error' : ''}`}
                          placeholder="First Name"
                          style={{ paddingLeft: '46px' }}
                        />
                      </div>
                      {registerErrors.first_name && <span className="input-error-msg">{registerErrors.first_name}</span>}
                    </div>

                    <div className="custom-input-group">
                      <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Last Name *</label>
                      <div className="custom-input-wrapper">
                        <span className="custom-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          name="last_name"
                          value={registerData.last_name}
                          onChange={handleRegisterChange}
                          className={`custom-form-input ${registerErrors.last_name ? 'input-error' : ''}`}
                          placeholder="Last Name"
                          style={{ paddingLeft: '46px' }}
                        />
                      </div>
                      {registerErrors.last_name && <span className="input-error-msg">{registerErrors.last_name}</span>}
                    </div>
                  </div>

                  {/* Username */}
                  <div className="custom-input-group">
                    <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Username *</label>
                    <div className="custom-input-wrapper">
                      <span className="custom-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        name="username"
                        value={registerData.username}
                        onChange={handleRegisterChange}
                        className={`custom-form-input ${registerErrors.username ? 'input-error' : ''}`}
                        placeholder="Username"
                        autoComplete="username"
                        style={{ paddingLeft: '46px' }}
                      />
                    </div>
                    {registerErrors.username && <span className="input-error-msg">{registerErrors.username}</span>}
                  </div>

                  {/* Email Address + OTP Verification */}
                  <div className="custom-input-group">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[12px] font-extrabold text-slate-500 uppercase tracking-wider">Email Address *</label>
                      {emailVerified ? (
                        <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Verified
                        </span>
                      ) : (
                        countdown > 0 && (
                          <span className="text-[11px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-100">
                            Resend in {countdown}s
                          </span>
                        )
                      )}
                    </div>
                    <div className="relative flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-slate-400 pointer-events-none">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                        </span>
                        <input
                          type="email"
                          name="email"
                          value={registerData.email}
                          onChange={handleRegisterChange}
                          disabled={emailVerified}
                          className={`w-full pl-11 pr-4 py-3 border ${registerErrors.email ? 'border-red-400 bg-red-50/30' : emailVerified ? 'border-emerald-300 bg-emerald-50/20 text-slate-700 font-semibold' : 'border-slate-200 focus:border-teal-600 focus:ring-4 focus:ring-teal-500/10'} outline-none rounded-xl text-slate-800 text-[14px] font-semibold transition-all bg-slate-50/50 hover:bg-white focus:bg-white disabled:opacity-90`}
                          placeholder="email@example.com"
                          autoComplete="email"
                        />
                      </div>
                      {!emailVerified && (
                        <button
                          type="button"
                          onClick={handleSendOTP}
                          disabled={otpStep === 'sending' || countdown > 0 || !registerData.email.trim()}
                          className="px-4 py-3 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none text-white text-xs font-extrabold rounded-xl transition-all shadow-md shadow-teal-600/15 flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                        >
                          {otpStep === 'sending'
                            ? 'Sending...'
                            : otpStep === 'sent'
                            ? 'Resend OTP'
                            : 'Send OTP'}
                        </button>
                      )}
                    </div>
                    {registerErrors.email && <span className="text-red-500 text-[11.5px] font-bold mt-1">{registerErrors.email}</span>}
                    {otpSuccess && <span className="text-emerald-600 text-[11.5px] font-bold mt-1 block">{otpSuccess}</span>}
                    {otpError && <span className="text-red-500 text-[11.5px] font-bold mt-1 block">{otpError}</span>}

                    {/* Compact Single Row OTP Verification Field */}
                    {otpStep !== 'idle' && otpStep !== 'sending' && !emailVerified && (
                      <div className="mt-2.5 p-3 bg-gradient-to-r from-teal-50/90 via-slate-50/90 to-cyan-50/80 border border-teal-200/90 shadow-sm rounded-xl flex flex-col gap-2 transition-all duration-200 animate-fade-in w-full">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Enter 6-Digit OTP Code *
                          </label>
                          <span className="text-[10.5px] font-bold text-slate-400">Valid for 5 mins</span>
                        </div>

                        <div className="flex items-center gap-2 w-full">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={otp}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setOtp(val);
                                setOtpError('');
                              }}
                              placeholder="123456"
                              className="w-full h-10 px-3 bg-white border border-teal-300 rounded-lg text-center text-lg font-mono font-extrabold tracking-[0.35em] text-slate-900 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-500/20 shadow-inner placeholder:tracking-[0.1em] placeholder:text-slate-300"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => handleVerifyOTP()}
                            disabled={otpStep === 'verifying' || otp.length !== 6}
                            className="px-4 h-10 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] disabled:bg-slate-200 disabled:text-slate-400 text-white font-extrabold text-xs uppercase tracking-wider rounded-lg transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
                          >
                            {otpStep === 'verifying' ? (
                              'Verifying...'
                            ) : (
                              <>
                                Verify OTP
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Fields Below OTP - Disabled until Email is Verified */}
                  <fieldset disabled={!emailVerified} className={`contents transition-all duration-300 ${!emailVerified ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                    
                    {!emailVerified && (
                      <div className="p-2.5 bg-amber-50/90 border border-amber-200/80 rounded-xl flex items-center gap-2.5 text-[12px] font-semibold text-amber-800 text-left my-1 shadow-sm">
                        <div className="w-5 h-5 rounded-lg bg-amber-500/10 text-amber-700 flex items-center justify-center shrink-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </div>
                        <span>Verify your email via OTP</span>
                      </div>
                    )}

                    {/* Role & Phone */}
                    <div className="form-row">
                      <div className="custom-input-group">
                        <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Role *</label>
                        <div className="custom-input-wrapper">
                          <span className="custom-input-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                          </span>
                          <select
                            id="role"
                            name="role"
                            value={registerData.role}
                            onChange={handleRegisterChange}
                            className={`custom-form-input ${registerErrors.role ? 'input-error' : ''}`}
                            style={{ paddingLeft: '46px' }}
                          >
                            <option value="STUDENT">Student</option>
                            <option value="PARENT">Parent / Guardian</option>
                            <option value="FACULTY">Faculty</option>
                            <option value="ADMIN">Admin</option>
                            <option value="PRINCIPAL">Principal</option>
                            <option value="DEAN">Dean</option>
                            <option value="FINANCE">Finance</option>
                            <option value="HOSTEL_ADMIN">Hostel Admin</option>
                            <option value="LIBRARY_STAFF">Library Staff</option>
                            <option value="TRANSPORT_MANAGER">Transport Manager</option>
                            <option value="DRIVER">Driver</option>
                            <option value="recepsonist">Receptionist (OPD)</option>
                            <option value="LAB_TECH_MICROBIOLOGY">Lab - Microbiology</option>
                            <option value="LAB_TECH_PATHOLOGY">Lab - Pathology</option>
                            <option value="LAB_TECH_BIOCHEMISTRY">Lab - Biochemistry</option>
                            <option value="BILLING_MEMBER">Lab Billing Member</option>
                            <option value="SAMPLE_COLLECTOR">Sample Collector (Lab)</option>
                            <option value="BILLING_MANAGER">Billing Manager (Lab)</option>
                            <option value="REPORT_GENERATION">Report Generation</option>
                            <option value="GRIEVANCE_OFFICER">Grievance Officer</option>
                            <option value="COE">Controller of Examinations</option>
                            <option value="PAPER_EVALUATOR">Paper Evaluator</option>
                          </select>
                        </div>
                        {registerErrors.role && <span className="input-error-msg">{registerErrors.role}</span>}
                      </div>

                      <div className="custom-input-group">
                        <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Phone Number</label>
                        <div className="custom-input-wrapper">
                          <span className="custom-input-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                          </span>
                          <input
                            type="tel"
                            name="phone_number"
                            value={registerData.phone_number}
                            onChange={handleRegisterChange}
                            className="custom-form-input"
                            placeholder="+91XXXXXXXXX"
                            style={{ paddingLeft: '46px' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ── PARENT-specific fields ── */}
                    {registerData.role === 'PARENT' && (
                      <>
                        <div className="parent-info-banner text-left">
                          <strong>👨‍👩‍👦 Parent / Guardian Registration</strong><br />
                          Search and link your child's student account below. You can link multiple children.
                        </div>

                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Your Relationship *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                            </span>
                            <select
                              id="relationship"
                              name="relationship"
                              value={registerData.relationship}
                              onChange={handleRegisterChange}
                              className="custom-form-input"
                              style={{ paddingLeft: '46px' }}
                            >
                              <option value="FATHER">Father</option>
                              <option value="MOTHER">Mother</option>
                              <option value="GUARDIAN">Guardian</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>
                        </div>

                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0, marginBottom: '2px' }}>
                            Link Student(s) * <span style={{ fontWeight:400, fontSize:11, color:'#64748b' }}>(search by name or ID)</span>
                          </label>
                          <StudentSearchDropdown
                            selectedStudents={selectedStudents}
                            onAdd={s => setSelectedStudents(p => p.find(x=>x.id===s.id) ? p : [...p, s])}
                            onRemove={id => setSelectedStudents(p => p.filter(s=>s.id!==id))}
                          />
                          {registerErrors.linked_students && <span className="input-error-msg" style={{ marginTop: '4px' }}>{registerErrors.linked_students}</span>}
                          {selectedStudents.length === 0 && (
                            <span style={{ fontSize:11.5, color:'#94a3b8', marginTop:6, display:'block', textAlign:'left' }}>No students linked yet. Please search and add.</span>
                          )}
                        </div>
                      </>
                    )}

                    {/* ── STUDENT-specific fields ── */}
                    {registerData.role === 'STUDENT' && (
                      <>
                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Course *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                              </svg>
                            </span>
                            <select
                              id="course"
                              name="course"
                              value={registerData.course}
                              onChange={handleRegisterChange}
                              className={`custom-form-input ${registerErrors.course ? 'input-error' : ''}`}
                              style={{ paddingLeft: '46px' }}
                            >
                              <option value="">Select Course</option>
                              <option value="MBBS">MBBS</option>
                              <option value="PG">Post Graduate (PG)</option>
                              <option value="SUPER_SPECIALITY">Super Speciality</option>
                              <option value="INTERNSHIP">Internship</option>
                            </select>
                          </div>
                          {registerErrors.course && <span className="input-error-msg">{registerErrors.course}</span>}
                          {registerData.course === 'MBBS' && (
                            <span className="info-message" style={{ color: '#008395', fontSize: '11px', marginTop: '4px', fontWeight: '600', textAlign: 'left' }}>
                              MBBS students are visible to all faculty members
                            </span>
                          )}
                        </div>

                        {['PG','SUPER_SPECIALITY'].includes(registerData.course) && (
                          <>
                            <div className="custom-input-group">
                              <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Department *</label>
                              <div className="custom-input-wrapper">
                                <span className="custom-input-icon">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <line x1="9" y1="3" x2="9" y2="21" />
                                  </svg>
                                </span>
                                <select
                                  id="student_department"
                                  name="student_department"
                                  value={registerData.student_department}
                                  onChange={handleRegisterChange}
                                  className={`custom-form-input ${registerErrors.student_department ? 'input-error' : ''}`}
                                  disabled={loadingDepartments || isDeptDisabled}
                                  style={{ paddingLeft: '46px' }}
                                >
                                  <option value="">{loadingDepartments ? 'Loading…' : 'Select Department'}</option>
                                  {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                              </div>
                              {registerErrors.student_department && <span className="input-error-msg">{registerErrors.student_department}</span>}
                            </div>

                            <div className="custom-input-group">
                              <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Assigned Professor *</label>
                              <div className="custom-input-wrapper">
                                <span className="custom-input-icon">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <polyline points="17 11 19 13 23 9" />
                                  </svg>
                                </span>
                                <select
                                  id="assigned_professor"
                                  name="assigned_professor"
                                  value={registerData.assigned_professor}
                                  onChange={handleRegisterChange}
                                  className={`custom-form-input ${registerErrors.assigned_professor ? 'input-error' : ''}`}
                                  disabled={!registerData.student_department || loadingProfessors}
                                  style={{ paddingLeft: '46px' }}
                                >
                                  <option value="">{loadingProfessors ? 'Loading…' : 'Select Professor'}</option>
                                  {professors.map(p => <option key={p.id} value={p.id}>{p.full_name} {p.employee_id?`(${p.employee_id})`:''} {p.subrole?`- ${p.subrole.replace(/_/g,' ')}`:''}</option>)}
                                </select>
                              </div>
                              {registerErrors.assigned_professor && <span className="input-error-msg">{registerErrors.assigned_professor}</span>}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* ── FACULTY-specific fields ── */}
                    {registerData.role === 'FACULTY' && (
                      <>
                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Department *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <line x1="9" y1="3" x2="9" y2="21" />
                              </svg>
                            </span>
                            <select
                              id="faculty_department"
                              name="faculty_department"
                              value={registerData.faculty_department}
                              onChange={handleRegisterChange}
                              className={`custom-form-input ${registerErrors.faculty_department ? 'input-error' : ''}`}
                              disabled={loadingDepartments}
                              style={{ paddingLeft: '46px' }}
                            >
                              <option value="">{loadingDepartments ? 'Loading…' : 'Select Department'}</option>
                              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </div>
                          {registerErrors.faculty_department && <span className="input-error-msg">{registerErrors.faculty_department}</span>}
                        </div>

                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Faculty Sub-Role *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                              </svg>
                            </span>
                            <select
                              id="faculty_subrole"
                              name="faculty_subrole"
                              value={registerData.faculty_subrole}
                              onChange={handleRegisterChange}
                              className={`custom-form-input ${registerErrors.faculty_subrole ? 'input-error' : ''}`}
                              style={{ paddingLeft: '46px' }}
                            >
                              <option value="">Select Sub-Role</option>
                              <option value="SENIOR_RESIDENT">Senior Resident</option>
                              <option value="ASSISTANT_PROFESSOR">Assistant Professor</option>
                              <option value="ASSOCIATE_PROFESSOR">Associate Professor</option>
                              <option value="PROFESSOR">Professor</option>
                              <option value="HEAD_OF_DEPARTMENT">Head of Department</option>
                              <option value="PROFESSOR_HOD">Professor + HOD</option>
                              <option value="DEAN">Dean</option>
                            </select>
                          </div>
                          {registerErrors.faculty_subrole && <span className="input-error-msg">{registerErrors.faculty_subrole}</span>}
                        </div>
                      </>
                    )}

                    {/* ── PAPER EVALUATOR-specific fields ── */}
                    {registerData.role === 'PAPER_EVALUATOR' && (
                      <>
                        <div className="parent-info-banner text-left">
                          <strong>📝 Paper Evaluator Registration</strong><br />
                          Please provide your University and State details. Your registration requires explicit approval from the Controller of Examinations (COE).
                        </div>

                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>University *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                              </svg>
                            </span>
                            <input
                              type="text"
                              id="evaluator_university"
                              name="evaluator_university"
                              value={registerData.evaluator_university}
                              onChange={handleRegisterChange}
                              className={`custom-form-input ${registerErrors.evaluator_university ? 'input-error' : ''}`}
                              placeholder="e.g. Harvard Medical School"
                              style={{ paddingLeft: '46px' }}
                            />
                          </div>
                          {registerErrors.evaluator_university && <span className="input-error-msg">{registerErrors.evaluator_university}</span>}
                        </div>

                        <div className="custom-input-group">
                          <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>State *</label>
                          <div className="custom-input-wrapper">
                            <span className="custom-input-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                              </svg>
                            </span>
                            <input
                              type="text"
                              id="evaluator_state"
                              name="evaluator_state"
                              value={registerData.evaluator_state}
                              onChange={handleRegisterChange}
                              className={`custom-form-input ${registerErrors.evaluator_state ? 'input-error' : ''}`}
                              placeholder="e.g. Karnataka"
                              style={{ paddingLeft: '46px' }}
                            />
                          </div>
                          {registerErrors.evaluator_state && <span className="input-error-msg">{registerErrors.evaluator_state}</span>}
                        </div>
                      </>
                    )}

                    {/* Password */}
                    <div className="custom-input-group">
                      <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Password *</label>
                      <div className="custom-input-wrapper">
                        <span className="custom-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </span>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={registerData.password}
                          onChange={handleRegisterChange}
                          className={`custom-form-input ${registerErrors.password ? 'input-error' : ''}`}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          style={{ paddingLeft: '46px' }}
                        />
                        <button
                          type="button"
                          className="custom-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {registerErrors.password && <span className="input-error-msg">{registerErrors.password}</span>}
                    </div>

                    {/* Confirm Password */}
                    <div className="custom-input-group">
                      <label className="custom-input-label" style={{ position: 'static', padding: 0 }}>Confirm Password *</label>
                      <div className="custom-input-wrapper">
                        <span className="custom-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </span>
                        <input
                          type={showConfirmPw ? 'text' : 'password'}
                          name="password_confirm"
                          value={registerData.password_confirm}
                          onChange={handleRegisterChange}
                          className={`custom-form-input ${registerErrors.password_confirm ? 'input-error' : ''}`}
                          placeholder="Confirm password"
                          autoComplete="new-password"
                          style={{ paddingLeft: '46px' }}
                        />
                        <button
                          type="button"
                          className="custom-password-toggle"
                          onClick={() => setShowConfirmPw(!showConfirmPw)}
                        >
                          {showConfirmPw ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {registerErrors.password_confirm && <span className="input-error-msg">{registerErrors.password_confirm}</span>}
                    </div>
                  </fieldset>

                {/* Submit Register */}
                <button
                  type="submit"
                  className="w-full bg-[#0D8AA8] hover:bg-[#0A6D8C] active:scale-[0.99] text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg shadow-teal-500/10 flex items-center justify-center relative text-[15px] cursor-pointer disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed mt-2 lg:flex-shrink-0"
                  disabled={registerLoading || !emailVerified}
                >
                  {registerLoading ? 'Creating Account…' : 'Create Account'}
                  <span className="absolute right-5 flex items-center text-white/95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </button>
              </form>
            )}

            {/* Bottom Form Actions */}
            <div className="flex flex-col gap-3 lg:flex-shrink-0">
              <div className="flex items-center justify-center my-1.5 gap-3.5">
                <div className="h-[1px] flex-1 bg-slate-100" />
                <span className="text-[12px] font-extrabold text-slate-400 tracking-wider uppercase">or</span>
                <div className="h-[1px] flex-1 bg-slate-100" />
              </div>

              {!isRegister ? (
                <Link
                  to="/register"
                  className="w-full h-12 border border-slate-200 hover:border-[#0D8AA8] rounded-xl text-[#0D8AA8] hover:text-[#0A6D8C] hover:bg-slate-50/50 font-bold text-[14px] transition-all flex items-center justify-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                  </svg>
                  Create Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="w-full h-12 border border-slate-200 hover:border-[#0D8AA8] rounded-xl text-[#0D8AA8] hover:text-[#0A6D8C] hover:bg-slate-50/50 font-bold text-[14px] transition-all flex items-center justify-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                  Already have an account? Sign In
                </Link>
              )}
            </div>

            {/* Enterprise Security Footer */}
            <div className="flex items-center justify-center gap-2.5 mt-2 text-slate-400 lg:flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-slate-400">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 11 12 14 15 10" />
              </svg>
              <span className="text-[11.5px] font-bold text-slate-500 text-left leading-tight">
                Your data is protected with<br />
                <strong className="text-slate-600 font-extrabold">enterprise-grade security</strong>
              </span>
            </div>

          </div>
        </div>
      </div>
  );
};

export default LoginPage;